import { EnpBookAndChapter, EnpFeature, VesselEnpBookAndChapterModelBase } from './vessel-enp-book-and-chapter-model';
import { RouteGeometry } from '../models/voyage-routing-model';
import { CrossedGeometries } from '../utils/geometries-intersection';

export class VesselEnpBookAndChapterModelImpl extends VesselEnpBookAndChapterModelBase {
    private _enpBookAndChapter: EnpBookAndChapter | null = null;
    private _books: Map<string, EnpFeature> | null = null;
    private _booksFeatures: EnpFeature[] | null = null;
    private _chapters: EnpFeature[] | null = null;

    private readonly _localStorageId: string = 'vessel-enp-book-and-chapter-model';

    constructor(enpGeojson?: EnpBookAndChapter) {
        super();

        if (enpGeojson) {
            this._enpBookAndChapter = enpGeojson;
        } else {
            this.loadingPromise = this._load();
            setInterval(() => this._loadGeoJson(), 60*60*1000); // refresh the list every 1h
        }
    }

    private _store() {
        localStorage.setItem(this._localStorageId, JSON.stringify(this._enpBookAndChapter));
    }

    private async _load() {
        try {
            const res = JSON.parse(localStorage.getItem(this._localStorageId) || "null");
            if (!res) {
                await this._loadGeoJson();
                return;
            }

            this._enpBookAndChapter = res;
        }
        catch (error) {
            await this._loadGeoJson();
        }
    }

    private async _loadGeoJson() {
        const enpbooks : EnpBookAndChapter = (await (await fetch("./enp-book-and-chapter-polygons.geojson")).json());
        if (enpbooks) {
            this._enpBookAndChapter = enpbooks;

            this._enpBookAndChapter.features.forEach(feature => {
                feature.properties.chapterName = unescape(feature.properties.chapterName!);
                feature.properties.title = unescape(feature.properties.title!);
            });

            this._store();
        }
    }

    get chapters() {
        if (!this._chapters) {
            this._chapters = this._enpBookAndChapter!.features.filter(feature => feature.properties.featureType === "BookChapter");
        }

        return this._chapters;
    }

    get books() {
        if (!this._books) {
            const books = this._enpBookAndChapter!.features.filter(feature => feature.properties.featureType === "Book");
            this._books = new Map(books.map((f): [string, EnpFeature] => [f.properties.catalogueNumber!, f]));
        }

        return this._books;
    }

    get booksFeatures() {
        if (!this._booksFeatures) {
            const books = this._enpBookAndChapter!.features.filter(feature => feature.properties.featureType === "Book");
            this._booksFeatures = books;
        }

        return this._booksFeatures;
    }

    chaptersCrossedByRoute(routeGeometry: RouteGeometry): Map<string, EnpFeature[]> {
        const crossedFeatures = CrossedGeometries(routeGeometry, this._enpBookAndChapter!.features!);
        const chaptersByBooks = new Map<string, EnpFeature[]>();
    
        Array.from(crossedFeatures).filter(feature => feature.properties.featureType === "BookChapter").forEach(feature => {
            const id = feature.properties.bookCatalogueNumber!;

            if (!chaptersByBooks.has(id)) {
                chaptersByBooks.set(id, []);
            }
            
            const existingEntries = chaptersByBooks.get(id!);
            chaptersByBooks.set(id, [...existingEntries!, feature]);
        });

        // detect empty books and add first chapter (by regulations)
        Array.from(crossedFeatures).filter(feature => feature.properties.featureType === "Book").forEach(feature => {
            const id = feature.properties.catalogueNumber!;

            if (!chaptersByBooks.has(id)) {
                const firstChapter : EnpFeature = {
                    "type": "Feature",
                    "properties": {
                        "featureType": "BookChapter",
                        "bookCatalogueNumber": `${id}`,
                        "chapterNumber": 1,
                        "chapterName": "",
                    }};

                
                chaptersByBooks.set(id, [firstChapter!]);
            }
        })
    
        return chaptersByBooks;
    }

    getCrossedEnpChaptersAndBooks(route: RouteGeometry) : string {
        const chaptersByBooks = this.chaptersCrossedByRoute(route);
        const sortedBooks = Array.from(chaptersByBooks.keys()).sort();

        let output = "";

        sortedBooks.forEach(b => {
            const book = this.books!.get(b);

            output += `${book!.properties.catalogueNumber} - ${book!.properties.title} - ${book!.properties.editionNumber} edition (${book!.properties.editionName})\n`;
            chaptersByBooks.get(book!.properties.catalogueNumber!)!
                           .sort((a, b) => a.properties.chapterNumber! - b.properties.chapterNumber!)
                           .forEach(c => {
                                output += ` - Chapter ${c.properties.chapterNumber} - ${c.properties.chapterName}\n`;
                            });
        });
        
        return output;
    }
}